import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { useMountEffect } from "../../../hooks/useMountEffect";
import { BreakPoint } from "../../../models/screen";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { getViewportBreakpoint } from "../../../utils/detectMobile";
import { GridAccordion } from "../GridAccordion";
import { updateSortMethod } from "../reducers";
import { getActiveSorter, selectSortMethod } from "../selectors";
import { GridSorterConfig } from "../sorters";

import styles from "../GridFilter/index.module.scss";

/**
 * The grid's sort options, as a sidebar accordion.
 *
 * The same sorters `TopNav` renders as a dropdown, in the shape the filters
 * beneath it use -- a collapsible heading over a list of options. Borrowing
 * `GridFilter`'s stylesheet rather than growing one of its own is the point:
 * a shopper reads Sort and Filter as one column of the same thing, and two
 * stylesheets would drift.
 *
 * Radio inputs, not checkboxes. A grid sorts one way at a time, and
 * `updateSortMethod` replaces rather than merges, so the markup says what the
 * state already does -- one of a set, arrow keys move between them, and there
 * is no way to express "no sort".
 */
export const GridSort = React.memo(
    (
        props: Immutable<{
            sorters: readonly GridSorterConfig[];
        }>,
    ): React.ReactNode => {
        const dispatch = useAppDispatch();
        const sortMethod = useAppSelector(selectSortMethod);
        const [isOpen, setIsOpen] = React.useState(true);

        // Collapsed on a small viewport, as the filters below are. Their
        // version dispatches, because two components render the same filter
        // and have to agree; nothing else renders this, so it stays local.
        useMountEffect(() => {
            if (getViewportBreakpoint() <= BreakPoint.MEDIUM) {
                setIsOpen(false);
            }
        });

        if (props.sorters.length <= 0) {
            return null;
        }

        // Through the same resolver the grid sorts by, so the checked option
        // is the one actually in effect -- including before anything has been
        // chosen, when that is the first sorter rather than none of them.
        const active = getActiveSorter(props.sorters, sortMethod);

        return (
            <GridAccordion
                bodyID={"grid-sort-body"}
                title={t`Sort`}
                isOpen={isOpen}
                onToggle={() => {
                    setIsOpen(!isOpen);
                }}
                headerClassName={"product-grid-sort__header"}
                bodyClassName={"product-grid-sort__body"}
            >
                {props.sorters.map((sorter) => {
                    const inputID = `grid-sort-${sorter.id}`;
                    return (
                        <div
                            key={sorter.id}
                            className={classNames({
                                [styles.filterOption]: true,
                                [`product-grid-sort__option--${sorter.id}`]: true,
                            })}
                        >
                            <input
                                id={inputID}
                                type="radio"
                                name="grid-sort"
                                className="product-grid-filter__input al-product-grid__sort-input"
                                value={sorter.id}
                                checked={sorter.id === active.id}
                                onChange={() => {
                                    dispatch(updateSortMethod(sorter.id));
                                }}
                            />
                            <label htmlFor={inputID}>
                                <span className={styles.filterOptionLabel}>
                                    {sorter.label}
                                </span>
                            </label>
                        </div>
                    );
                })}
            </GridAccordion>
        );
    },
);

GridSort.displayName = "GridSort";
